import {
  EnumProductTierConditionClausesComparator,
  EnumProductTierConditionOperator,
  type ProductTierCondition,
  type ProductTierConditionClauses,
} from "gql/generated";

type Answer = {
  questionId: string;
  values: string[];
};

function evaluateClause(clause: ProductTierConditionClauses, answers: Answer[]): boolean {
  const answer = answers.find(a => a.questionId === clause.questionId);
  if (!answer || answer.values.length === 0) return false;

  const joined = answer.values.join(" ").toLowerCase();
  const expected = clause.value.toLowerCase();

  switch (clause.comparator) {
    case EnumProductTierConditionClausesComparator.Equals:
      return answer.values.some(v => v.toLowerCase() === expected);
    case EnumProductTierConditionClausesComparator.NotEquals:
      return answer.values.every(v => v.toLowerCase() !== expected);
    case EnumProductTierConditionClausesComparator.Contains:
      return joined.includes(expected);
    case EnumProductTierConditionClausesComparator.In: {
      const allowed = expected.split(",").map((s: string) => s.trim());
      return answer.values.some(v => allowed.includes(v.toLowerCase()));
    }
    default:
      return false;
  }
}

function evaluateCondition(condition: ProductTierCondition, answers: Answer[]): boolean {
  const clauses = (condition.clauses ?? []).filter(
    (c): c is NonNullable<typeof c> => c != null,
  );
  if (clauses.length === 0) return true;

  if (condition.operator === EnumProductTierConditionOperator.And) {
    return clauses.every(c => evaluateClause(c, answers));
  }
  return clauses.some(c => evaluateClause(c, answers));
}

/**
 * Returns true if a tier should be visible given the current questionnaire answers.
 * Tiers without conditions always pass.
 */
export function isTierVisibleForAnswers(
  tierCondition: ProductTierCondition | null | undefined,
  answers: Answer[],
): boolean {
  if (!tierCondition) return true;
  return evaluateCondition(tierCondition, answers);
}
